import { type FormEvent, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router';
import { merkeGeradeAngemeldet } from './gerade-angemeldet.js';
import { useAuth } from './auth-context.js';
import { AuthRahmen } from './auth-rahmen.js';
import { AnmeldeFehler, type AnmeldeDienst } from './sign-in-service.js';

function meldungVon(fehler: unknown): string {
  return fehler instanceof AnmeldeFehler
    ? fehler.text
    : 'Die Anmeldung ist gescheitert. Bitte versuchen Sie es erneut.';
}

export function LoginPage({ dienst }: { dienst: AnmeldeDienst }) {
  const { nutzer } = useAuth();
  const ort = useLocation();
  const ziel = (ort.state as { ziel?: string } | null)?.ziel ?? '/';

  const [email, setEmail] = useState('');
  const [passwort, setPasswort] = useState('');
  const [faktorUid, setFaktorUid] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [meldung, setMeldung] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  if (nutzer) {
    return <Navigate to={ziel} replace />;
  }

  async function sende(schritt: () => Promise<void>) {
    setMeldung(null);
    setLaeuft(true);
    try {
      await schritt();
    } catch (fehler) {
      setMeldung(meldungVon(fehler));
    } finally {
      setLaeuft(false);
    }
  }

  function abschickenPasswort(ereignis: FormEvent) {
    ereignis.preventDefault();
    void sende(async () => {
      const ergebnis = await dienst.melde(email, passwort);
      if (ergebnis.art === 'zweiter-faktor') {
        setFaktorUid(ergebnis.faktorUid);
        setPasswort('');
        return;
      }
      merkeGeradeAngemeldet();
    });
  }

  function abschickenCode(ereignis: FormEvent) {
    ereignis.preventDefault();
    void sende(async () => {
      await dienst.loeseZweitenFaktor(faktorUid ?? '', code);
      merkeGeradeAngemeldet();
    });
  }

  return (
    <AuthRahmen>
      <h1>Anmelden</h1>
      <p className="eds-text-secondary">Interne Plattform für SEO-Ratgeber, Bilder und Analysen.</p>

      {meldung && (
        <div className="eds-alert eds-alert--error" role="alert">
          <p>{meldung}</p>
        </div>
      )}

      {faktorUid === null ? (
        <form onSubmit={abschickenPasswort}>
          <div className="eds-field">
            <label className="eds-field__label" htmlFor="email">
              E-Mail
            </label>
            <input
              className="eds-field__input"
              id="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="eds-field">
            <label className="eds-field__label" htmlFor="passwort">
              Passwort
            </label>
            <input
              className="eds-field__input"
              id="passwort"
              type="password"
              autoComplete="current-password"
              required
              value={passwort}
              onChange={(e) => setPasswort(e.target.value)}
            />
          </div>
          <button className="eds-button eds-button--primary" type="submit" disabled={laeuft}>
            Anmelden
          </button>
          <Link to="/passwort-vergessen">Passwort vergessen?</Link>
        </form>
      ) : (
        <form onSubmit={abschickenCode}>
          <p>Bitte geben Sie den Code aus Ihrer Authenticator-App ein.</p>
          <div className="eds-field">
            <label className="eds-field__label" htmlFor="code">
              Sechsstelliger Code
            </label>
            <input
              className="eds-field__input"
              id="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </div>
          <button className="eds-button eds-button--primary" type="submit" disabled={laeuft}>
            Bestätigen
          </button>
        </form>
      )}
    </AuthRahmen>
  );
}
